import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ClientAvatar } from "@/components/ClientAvatar";
import { PropertyThumb } from "@/components/PropertyThumb";
import { DropboxLogoSearch } from "@/components/DropboxLogoSearch";
import { BoundaryReference } from "@/components/BoundaryReference";
import { PropertyPhotos } from "@/components/PropertyPhotos";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DeliverableBadge, PaymentBadge, DeliverableStatusBadge } from "@/components/Badges";
import { EditorBubble } from "@/components/EditorBubble";
import OrderMusicPanel from "@/components/OrderMusicPanel";
import OrderTemplatesPanel from "@/components/OrderTemplatesPanel";
import DueDatePicker from "@/components/DueDatePicker";
import { formatCurrency, formatDate, formatDateTime, formatTime, relativeDue, formatDuration, formatClock } from "@/lib/constants";
import {
  ArrowLeft, MapPin, Mail, Phone, ExternalLink, Loader2, Save, Camera, CalendarClock, Clock, UserCog,
  Images, Film, Ruler, Box, Cloud, Folder, AlertTriangle, History, CheckCircle2, Copy, ChevronDown, Play, Square, Pencil,
  Search, RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTimer } from "@/context/TimerContext";
import { toast } from "sonner";

const DELIV_STATUSES = ["NEW", "EDITING", "READY", "DELIVERED"];

// Maps a deliverable type to the Aryeo media category it counts + its icon/noun.
const MEDIA_META = {
  "Photos": { noun: "images", Icon: Images },
  "Aerial Photos": { noun: "images", Icon: Images },
  "Photo Edits": { noun: "images", Icon: Images },
  "Video": { noun: "videos", Icon: Film },
  "Aerial Videos": { noun: "videos", Icon: Film },
  "Social Reels": { noun: "videos", Icon: Film },
  "Photo to Video": { noun: "videos", Icon: Film },
  "Floor Plans": { noun: "floor plans", Icon: Ruler },
  "3D Tours": { noun: "tours", Icon: Box },
};

function MediaCount({ type, count }) {
  const meta = MEDIA_META[type];
  if (!meta || count == null || count <= 0) return null;
  const noun = count === 1 ? meta.noun.replace(/s$/, "") : meta.noun;
  const Icon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-[11px] font-medium text-foreground/80"
      data-testid="deliverable-media-count">
      <Icon className="h-3 w-3 text-muted-foreground" />{count} {noun}
    </span>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editors, setEditors] = useState([]);
  const [dbxConnected, setDbxConnected] = useState(false);

  const [refreshing, setRefreshing] = useState(false);
  const refreshFromAryeo = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/orders/${id}/refresh`);
      setOrder(data);
      toast.success("Order refreshed from Aryeo");
    } catch (err) { toast.error(apiError(err)); }
    setRefreshing(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setNotes(data.notes || "");
    } catch (err) {
      toast.error(apiError(err));
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/editors").then(({ data }) => setEditors(data)).catch((err) => console.error("Failed to load editors", err)); }, []);
  useEffect(() => { api.get("/dropbox/status").then(({ data }) => setDbxConnected(data.connected)).catch(() => {}); }, []);

  const changeDeliverable = async (delivId, status) => {
    setOrder((o) => ({ ...o, deliverables: o.deliverables.map((d) => d.id === delivId ? { ...d, status } : d) }));
    try {
      await api.patch(`/orders/${id}/deliverables/${delivId}`, { status });
    } catch (err) { toast.error(apiError(err)); load(); }
  };

  const changeDeliverableDue = async (delivId, due) => {
    setOrder((o) => ({ ...o, deliverables: o.deliverables.map((d) => (d.id === delivId || d.aryeo_item_id === delivId) ? { ...d, due_date: due || null } : d) }));
    try {
      const { data } = await api.patch(`/orders/${id}/deliverables/${delivId}`, { due_date: due });
      setOrder(data);
      toast.success(due ? "Due date set" : "Reverted to auto due date");
    } catch (err) { toast.error(apiError(err)); load(); }
  };

  const changeDeliverableEditor = async (delivId, editorId) => {
    try {
      const { data } = await api.patch(`/orders/${id}/deliverables/${delivId}`, { editor_id: editorId });
      setOrder(data);
    } catch (err) { toast.error(apiError(err)); load(); }
  };

  const [showUntracked, setShowUntracked] = useState(false);
  const timer = useTimer();
  const [editTime, setEditTime] = useState(null); // { delivId, h, m }
  const [dbxFolders, setDbxFolders] = useState(null);
  const [dbxFoldersLoading, setDbxFoldersLoading] = useState(false);

  const loadDbxFolders = async () => {
    setDbxFoldersLoading(true);
    try {
      const { data } = await api.post(`/orders/${id}/dropbox-folders`);
      setDbxFolders(data.folders || []);
    } catch (err) {
      toast.error(apiError(err));
      setDbxFolders([]);
    } finally {
      setDbxFoldersLoading(false);
    }
  };

  useEffect(() => { if (order) setDbxFolders(order.dbx_folders || null); }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startTimer = async (delivId) => {
    try {
      await timer.start(id, delivId);
      load(); // refresh times (a previously-running product just accumulated)
    } catch (err) { toast.error(apiError(err)); }
  };
  const stopTimer = async () => {
    try {
      await timer.stop();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const saveEditTime = async () => {
    if (!editTime) return;
    const seconds = (parseInt(editTime.h, 10) || 0) * 3600 + (parseInt(editTime.m, 10) || 0) * 60;
    try {
      const { data } = await api.patch(`/orders/${id}/deliverables/${editTime.delivId}`, { time_seconds: seconds });
      setOrder(data);
      setEditTime(null);
      toast.success("Tracked time updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleDeliverableTracked = async (delivId, tracked) => {    try {
      const { data } = await api.patch(`/orders/${id}/deliverables/${delivId}`, { tracked });
      setOrder(data);
      toast.success(tracked ? "Added back to workflow tracking" : "Excluded from workflow tracking");
    } catch (err) { toast.error(apiError(err)); load(); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/orders/${id}`, { notes });
      toast.success("Notes saved");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingNotes(false); }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Couldn't copy to clipboard"); }
  };

  if (loading) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!order) return null;

  return (
    <div className="mx-auto max-w-5xl p-6 md:p-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/orders")} data-testid="back-btn"
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to orders
        </button>
        {(order.listing_url || order.status_url) && (
          <div className="flex items-center gap-2">
          <button onClick={refreshFromAryeo} disabled={refreshing} data-testid="aryeo-refresh-btn"
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:bg-secondary disabled:opacity-50">
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh from Aryeo
          </button>
          <a href={order.listing_url || order.status_url} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-secondary"
            data-testid="aryeo-link-top">
            View in Aryeo <ExternalLink className="h-3 w-3" />
          </a>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
        <PropertyThumb orderId={order.id} show={order.thumb_cached} className="h-14 w-[75px] rounded-lg sm:h-[90px] sm:w-[120px]" testId="order-property-thumb" />
        <div>
          <span className="font-mono text-xs text-muted-foreground">#{order.number}</span>
          <h1 className="mt-1 flex items-start gap-2 font-heading text-2xl font-bold tracking-tight">
            <MapPin className="mt-1.5 h-5 w-5 shrink-0 text-primary" />
            <span>{order.address || order.title}</span>
            <button onClick={() => copyToClipboard(order.address || order.title, "Address")}
              data-testid="copy-address-btn" title="Copy address"
              className="mt-1.5 shrink-0 text-muted-foreground transition-colors hover:text-primary">
              <Copy className="h-4 w-4" />
            </button>
          </h1>
          {order.client_name && (
            <div className="mt-1.5 flex items-center gap-1.5 pl-7 text-sm text-muted-foreground" data-testid="order-client-name">
              <ClientAvatar name={order.client_name} email={order.client_email} className="h-5 w-5 text-[8px]" testId="order-header-client-avatar" />{order.client_name}
            </div>
          )}
        </div>
        </div>
        <div className="flex items-center gap-2">
          <PaymentBadge status={order.payment_status} />
          {order.shoot_cancelled ? (
            <span data-testid="shoot-cancelled-badge" className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs font-medium text-rose-600 dark:text-rose-300">
              <AlertTriangle className="h-3.5 w-3.5" /> Shoot cancelled
            </span>
          ) : (() => {
            const tracked = (order.deliverables || []).filter((d) => d.due_source !== "excluded");
            const done = tracked.filter((d) => d.status === "DELIVERED").length;
            const totalSecs = (order.deliverables || []).reduce((s, d) => s + (d.time_seconds || 0), 0);
            return (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-medium" data-testid="order-progress">
                  {done}/{tracked.length} products done
                </span>
                {totalSecs > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-medium" data-testid="order-total-time">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" /> {formatDuration(totalSecs)} tracked
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-border" data-testid="order-tabs">
        {[["details", "Details"], ["photos", "Photos"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`order-tab-${k}`}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "photos" && <div className="mt-6"><PropertyPhotos order={order} /></div>}
      {tab === "details" && <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Deliverables */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card" data-testid="deliverables-panel">
            <div className="border-b border-border px-5 py-4">
              <span className="overline text-muted-foreground">Deliverables</span>
            </div>
            <div className="divide-y divide-border">
              {(() => {
                const dels = order.deliverables || [];
                if (dels.length === 0) return (
                  <div className="px-5 py-8 text-center text-sm text-muted-foreground">No deliverables on this order.</div>
                );
                const isUntracked = (d) => d.due_source === "excluded" || d.tracked === false;
                const renderRow = (d) => {
                  const manualUntracked = d.tracked === false;
                  const isExcluded = d.due_source === "excluded" || manualUntracked;
                  const typeExcluded = isExcluded && !manualUntracked;
                  const delivId = d.id || d.aryeo_item_id;
                  return (
                  <div key={d.id || d.aryeo_item_id} className={`flex items-stretch ${isExcluded ? "opacity-60" : ""}`} data-testid={`deliverable-${d.id || d.aryeo_item_id}`}>
                    <div className="min-w-0 flex-1 space-y-2.5 py-3.5 pl-5 pr-3">
                    <div className="min-w-0">
                      <div className={`text-sm font-medium ${isExcluded ? "line-through" : ""}`}>{d.title}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <DeliverableBadge type={d.type} />
                        {d.quantity > 1 && <span className="text-[11px] text-muted-foreground">×{d.quantity}</span>}
                        <MediaCount type={d.type} count={d.media_count} />
                        {isExcluded && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-slate-500/40 bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300" data-testid={`not-tracked-${d.id || d.aryeo_item_id}`}>
                            Not tracked
                          </span>
                        )}
                        {!isExcluded && d.editor_name && (
                          <EditorBubble name={d.editor_name} color={d.editor_color} link={d.editor_link}
                            className="px-2 py-0.5 text-[11px]" iconClass="h-3 w-3" testid={`editor-bubble-${d.id || d.aryeo_item_id}`} />
                        )}
                        {!isExcluded && d.due_date && d.status !== "DELIVERED" && (
                          <span title={formatDate(d.due_date)}
                            className={`text-xs font-semibold ${d.due_date < new Date().toISOString().slice(0, 10) ? "text-rose-500 dark:text-rose-400" : "text-foreground"}`}>
                            {relativeDue(d.due_date)}
                          </span>
                        )}
                        {!isExcluded && d.due_source === "auto" && d.due_date && (
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">auto</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!isExcluded && (
                        <>
                          <Select value={d.editor_id || "none"} onValueChange={(v) => changeDeliverableEditor(delivId, v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`deliverable-editor-${delivId}`}>
                              <SelectValue placeholder="Assign editor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">Unassigned</SelectItem>
                              {editors.map((e) => <SelectItem key={e.id} value={e.id} className="text-xs">{e.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <DueDatePicker
                            value={d.due_date}
                            onChange={(due) => changeDeliverableDue(delivId, due)}
                            testid={`deliverable-due-${delivId}`}
                          />
                          <Select value={d.status} onValueChange={(v) => changeDeliverable(delivId, v)}>
                            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`deliverable-status-${delivId}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {DELIV_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">
                                {{ NEW: "New", EDITING: "Editing", READY: "Ready", DELIVERED: "Delivered" }[s]}
                              </SelectItem>)}
                            </SelectContent>
                          </Select>
                        </>
                      )}
                      {typeExcluded && !isExcluded && (
                        <span className="whitespace-nowrap text-[11px] text-muted-foreground">tracked (type off in Settings)</span>
                      )}
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1.5 pl-1">
                              <Switch
                                checked={!isExcluded}
                                onCheckedChange={(v) => toggleDeliverableTracked(delivId, v)}
                                data-testid={`deliverable-track-${delivId}`}
                                className="scale-90"
                              />
                              <span className="text-[11px] font-medium text-muted-foreground">Track</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {isExcluded
                              ? (typeExcluded ? "This type is off in Settings — turn on to track this one anyway" : "Turn on to track this product in the workflow")
                              : "Turn off to exclude this product from tracking"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {!isExcluded && (() => {
                      const running = timer?.active && timer.active.order_id === id &&
                        (timer.active.deliverable_id === d.id || timer.active.deliverable_id === delivId);
                      return (
                        <div className="flex items-center gap-2 border-t border-border/60 pt-2.5" data-testid={`timer-group-${delivId}`}>
                          {running ? (
                            <button onClick={stopTimer} data-testid={`timer-stop-${delivId}`}
                              className="flex h-7 items-center gap-1.5 rounded-md border border-rose-500/50 bg-rose-500/10 px-2.5 text-xs font-semibold text-rose-500 transition-colors hover:bg-rose-500/20">
                              <Square className="h-3 w-3 fill-current" /> Stop timer
                            </button>
                          ) : (
                            <button onClick={() => startTimer(delivId)} data-testid={`timer-start-${delivId}`}
                              className="flex h-7 items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-500/20 dark:text-emerald-400">
                              <Play className="h-3 w-3 fill-current" /> Start timer
                            </button>
                          )}
                          <Clock className="ml-1 h-3.5 w-3.5 text-muted-foreground" />
                          <span className={`font-mono text-xs tabular-nums ${running ? "font-semibold text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
                            data-testid={`timer-value-${delivId}`}>
                            {running ? formatClock(timer.elapsed) : formatDuration(d.time_seconds)}
                          </span>
                          <span className="text-[11px] text-muted-foreground">tracked</span>
                          <button onClick={() => setEditTime({ delivId, h: String(Math.floor((d.time_seconds || 0) / 3600)), m: String(Math.floor(((d.time_seconds || 0) % 3600) / 60)) })}
                            data-testid={`timer-edit-${delivId}`} title="Edit tracked time"
                            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })()}
                    </div>
                    {!isExcluded && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => changeDeliverable(delivId, d.status === "DELIVERED" ? "READY" : "DELIVERED")}
                              data-testid={`deliverable-deliver-${delivId}`}
                              className={`flex w-12 shrink-0 items-center justify-center border-l border-border transition-colors ${d.status === "DELIVERED" ? "bg-emerald-500/15 text-emerald-500" : "bg-background/40 text-muted-foreground hover:bg-emerald-500/20 hover:text-emerald-500"}`}>
                              <CheckCircle2 className="h-5 w-5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {d.status === "DELIVERED" ? "Delivered — click to undo" : "Mark delivered"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  );
                };
                const tracked = dels.filter((d) => !isUntracked(d));
                const untracked = dels.filter(isUntracked);
                return (
                  <>
                    {tracked.map(renderRow)}
                    {untracked.length > 0 && (
                      <div data-testid="untracked-group">
                        <button onClick={() => setShowUntracked((v) => !v)} data-testid="untracked-toggle"
                          className="flex w-full items-center justify-between gap-2 bg-secondary/50 px-5 py-3 text-left transition-colors hover:bg-secondary/80">
                          <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            Not tracked
                            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs" data-testid="untracked-count">{untracked.length}</span>
                          </span>
                          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showUntracked ? "rotate-180" : ""}`} />
                        </button>
                        {showUntracked && (
                          <div className="divide-y divide-border border-t border-border" data-testid="untracked-list">
                            {untracked.map(renderRow)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Dropbox */}
          <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="dropbox-panel">
            <div className="flex items-center justify-between">
              <span className="overline text-muted-foreground flex items-center gap-1.5"><Cloud className="h-3.5 w-3.5 text-blue-400" /> Dropbox</span>
            </div>
            {!dbxConnected && (
              <p className="mt-3 text-sm text-muted-foreground">
                Connect Dropbox in <button onClick={() => navigate("/integrations")} className="text-primary underline">Integrations</button> to see this property's folders.
              </p>
            )}
            {dbxConnected && (
              <div className="mt-3" data-testid="dbx-related-folders">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Property folders</span>
                  <button onClick={loadDbxFolders} disabled={dbxFoldersLoading} data-testid="dbx-folders-refresh"
                    className="inline-flex items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-300 transition-colors hover:bg-blue-500/20 disabled:opacity-50">
                    {dbxFoldersLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : (dbxFolders && dbxFolders.length ? <RefreshCw className="h-3.5 w-3.5" /> : <Search className="h-3.5 w-3.5" />)}
                    {dbxFoldersLoading ? "Searching…" : (dbxFolders && dbxFolders.length ? "Refresh" : "Search")}
                  </button>
                </div>
                {dbxFoldersLoading ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching folders…</div>
                ) : dbxFolders && dbxFolders.length ? (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {dbxFolders.map((f) => (
                      <div key={f.link} className="rounded-lg border border-border p-2.5" data-testid={`dbx-folder-${f.label}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                            {f.label === "Video" ? <Film className="h-3.5 w-3.5 text-purple-400" />
                              : f.label === "RAW" ? <Box className="h-3.5 w-3.5 text-amber-400" />
                              : f.label === "JPG" ? <Images className="h-3.5 w-3.5 text-sky-400" />
                              : f.label === "Done" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                              : <Folder className="h-3.5 w-3.5" />}
                            {f.label}
                          </span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">{f.count} files{f.modified ? ` · ${formatDate(f.modified)}` : ""}</span>
                        </div>
                        <div dir="rtl" className="mt-1 truncate text-left text-[11px] text-muted-foreground" title={f.path}>{"\u200e" + f.path}</div>
                        <div className="mt-1.5 flex gap-2">
                          <a href={f.link} target="_blank" rel="noreferrer" data-testid={`dbx-folder-open-${f.label}`}
                            className="flex flex-1 items-center justify-center gap-1 rounded border border-blue-500/40 bg-blue-500/10 py-1 text-[11px] font-medium text-blue-300 transition-colors hover:bg-blue-500/20">
                            Open <ExternalLink className="h-3 w-3" />
                          </a>
                          <button onClick={() => copyToClipboard(f.link, `${f.label} link`)} data-testid={`dbx-folder-copy-${f.label}`}
                            className="flex items-center justify-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : dbxFolders ? (
                  <p className="mt-2 text-xs text-muted-foreground">No matching property folders found.</p>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">Tap “Search” to find and save this property's Dropbox folders.</p>
                )}
                <DropboxLogoSearch orderId={id}
                  videoFolder={(dbxFolders || []).filter((f) => f.label === "Video").map((f) => f.path).pop() || order.dropbox_folder_path} />
              </div>
            )}
          </div>

          <div className="mt-6">
            <OrderMusicPanel order={order} setOrder={setOrder} />
          </div>

          <div className="mt-6">
            <OrderTemplatesPanel order={order} />
          </div>
        </div>

        {/* Client + summary */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="appointment-panel">
            <span className="overline text-muted-foreground">Shoot / Appointment</span>
            {(order.appointments || []).length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No appointment scheduled.</p>
            ) : (() => {
              const appts = order.appointments || [];
              const activeAppts = appts.filter((a) => !a.cancelled && a.start_at);
              const isReshoot = activeAppts.length > 1;
              const currentStart = order.shoot_date;
              const prev = activeAppts.filter((a) => a.start_at !== currentStart);
              return (
              <>
                {isReshoot && prev.length > 0 && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="reshoot-note">
                    <History className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>Reshoot — this property was also shot on {prev.map((a) => new Date(a.start_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })).join(", ")}. Product due dates follow the latest shoot.</span>
                  </div>
                )}
                <div className="mt-3 space-y-3">
                {appts.map((a, i) => {
                  const isCurrent = !a.cancelled && a.start_at === currentStart;
                  const isPrevious = !a.cancelled && !isCurrent;
                  return (
                  <div key={a.start_at || `appt-${i}`} className={`rounded-lg border p-3 ${a.cancelled ? "border-rose-500/40 bg-rose-500/5" : isPrevious ? "border-border bg-secondary/20 opacity-70" : "border-border bg-secondary/40"}`} data-testid={`appointment-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className={`flex items-center gap-2 text-sm font-medium ${a.cancelled ? "line-through opacity-70" : ""}`}>
                        <CalendarClock className="h-4 w-4 text-primary" />
                        {a.start_at ? new Date(a.start_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "TBD"}
                      </div>
                      {isReshoot && isCurrent && (
                        <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300" data-testid={`appointment-current-${i}`}>Current shoot</span>
                      )}
                      {isPrevious && (
                        <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid={`appointment-previous-${i}`}>Previous shoot</span>
                      )}
                    </div>
                    {a.start_at && (
                      <div className={`mt-1.5 flex items-center gap-2 text-sm text-muted-foreground ${a.cancelled ? "line-through opacity-70" : ""}`}>
                        <Clock className="h-3.5 w-3.5" />
                        {formatTime(a.start_at)}{a.end_at ? ` – ${formatTime(a.end_at)}` : ""}
                        {a.duration ? ` · ${a.duration} min` : ""}
                      </div>
                    )}
                    {a.previous_start_at && a.rescheduled_at && (
                      <div className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-300">
                        Rescheduled — was {new Date(a.previous_start_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </div>
                    )}
                    {a.photographers?.length > 0 && (
                      <div className="mt-1.5 flex items-center gap-2 text-sm">
                        <Camera className="h-3.5 w-3.5 text-primary" />
                        <span className="font-medium">{a.photographers.join(", ")}</span>
                      </div>
                    )}
                    {a.status && (
                      a.cancelled ? (
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[11px] font-medium text-rose-600 dark:text-rose-300" data-testid={`appointment-status-${i}`}>
                          <AlertTriangle className="h-3 w-3" /> {a.status}
                        </div>
                      ) : (
                        <div className="mt-1.5 overline text-muted-foreground" data-testid={`appointment-status-${i}`}>{a.status}</div>
                      )
                    )}
                  </div>
                  );
                })}
                </div>
              </>
              );
            })()}
          </div>

          {(order.deliverables || []).some((d) => d.type === "Property Lines") && <BoundaryReference orderId={id} />}
          {/* Notes */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="notes-panel">
            <span className="overline text-muted-foreground">Internal Notes</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
              placeholder="Add production notes for this order…" className="mt-3 resize-none" data-testid="notes-input" />
            <Button onClick={saveNotes} disabled={savingNotes} size="sm" className="mt-3 gap-2" data-testid="save-notes-btn">
              {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save notes
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-card p-5" data-testid="client-panel">
            <span className="overline text-muted-foreground">Client</span>
            <div className="mt-3 flex items-center gap-2 text-sm font-medium">
              {order.client_name && <ClientAvatar name={order.client_name} email={order.client_email} className="h-8 w-8 text-[11px]" testId="order-panel-client-avatar" />}{order.client_name || "—"}
            </div>
            {order.client_email && (
              <a href={`mailto:${order.client_email}`} className="mt-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <Mail className="h-3.5 w-3.5" />{order.client_email}
              </a>
            )}
            {order.client_phone && (
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />{order.client_phone}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5" data-testid="summary-panel">
            <span className="overline text-muted-foreground">Order Summary</span>
            <dl className="mt-3 space-y-2.5 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Total</dt><dd className="font-mono">{formatCurrency(order.total_amount, order.currency)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Fulfillment</dt><dd>{order.fulfillment_status}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Created</dt><dd>{formatDate(order.aryeo_created_at)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Synced</dt><dd>{formatDate(order.synced_at)}</dd></div>
            </dl>
          </div>
        </div>
      </div>}

      <Dialog open={!!editTime} onOpenChange={(o) => !o && setEditTime(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="edit-time-dialog">
          <DialogHeader>
            <DialogTitle>Edit tracked time</DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="edit-time-h" className="text-xs text-muted-foreground">Hours</Label>
              <Input id="edit-time-h" type="number" min="0" value={editTime?.h ?? ""} data-testid="edit-time-hours"
                onChange={(e) => setEditTime((s) => ({ ...s, h: e.target.value }))} className="mt-1" />
            </div>
            <div className="flex-1">
              <Label htmlFor="edit-time-m" className="text-xs text-muted-foreground">Minutes</Label>
              <Input id="edit-time-m" type="number" min="0" max="59" value={editTime?.m ?? ""} data-testid="edit-time-minutes"
                onChange={(e) => setEditTime((s) => ({ ...s, m: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <DialogFooter className="mt-2 gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setEditTime({ ...editTime, h: "0", m: "0" })} data-testid="edit-time-reset" className="mr-auto">Reset to 0</Button>
            <Button variant="outline" onClick={() => setEditTime(null)} data-testid="edit-time-cancel">Cancel</Button>
            <Button onClick={saveEditTime} data-testid="edit-time-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
